import React, { useContext, useState } from "react";
import css from "./EnquiryForm.module.scss";
import EnquiryFormMessageArea from "./EnquiryFormMessageArea";
import EnquiryFormUserDetails from "./EnquiryFormUserDetails";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Button from "@ui/Button";
import { SubmitHandler, useFormContext } from "react-hook-form";
import { EnquiryFormType } from "./types";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { EnquiryFormContext } from "./EnquiryForm";
import Show from "@ui/Show";
import EnquiryFormHeader from "./Header";

interface Props {
    supplierName: string,
    redirectAnchor?: string;
    onSubmit: (data: EnquiryFormType, masterVenueUpdated: boolean) => Promise<void>;
    textOptions: {
        tags: string[];
        suggestions: Record<string, string>;
    }
}

const EnquiryForm = ({
    supplierName,
    textOptions,
    redirectAnchor,
    onSubmit
}: Props) => {
    const [masterVenueUpdated, setMasterVenueUpdated] = useState<boolean>(false);
    const { handleSubmit, formState: { isSubmitting } } = useFormContext<EnquiryFormType>();
    const { isSupplierMessaging } = useContext(EnquiryFormContext);

    const onFormSubmit: SubmitHandler<EnquiryFormType> = async (data) => {
        // TODO: Maybe we can handle master venue change by checking isDirty field on submit
        await onSubmit(data, masterVenueUpdated);
    }
    const preventFormSubmitOnEnter = (
        event: React.KeyboardEvent<HTMLFormElement>
    ) => {
        if (event.key === "Enter") {
            event.preventDefault();
        }
    };

    return (
        <form
            className={css.container}
            onSubmit={handleSubmit(onFormSubmit)}
            onKeyDown={preventFormSubmitOnEnter}
        >
            <EnquiryFormHeader supplierName={supplierName} isSupplierMessaging={isSupplierMessaging} />
            <div className={css.userData}>
                <EnquiryFormMessageArea
                    tags={textOptions.tags}
                    suggestions={textOptions.suggestions}
                    defaultSuggestion={''}
                />
                <EnquiryFormUserDetails
                    redirectAnchor={redirectAnchor}
                    setMasterVenueUpdated={setMasterVenueUpdated}
                />
                <div className={css.cta}>
                    <Show when={isSupplierMessaging}>
                        <div className={`small pb-3 ${css.whyEW}`}>
                            <OverlayTrigger overlay={<Tooltip className={css.tooltip}>
                                    <ul>
                                    <li>Track all of your wedding supplier conversations and bookings in one place.</li>
                                    <li>Enquire with new suppliers in just one click</li>
                                    <li>Chat with suppliers on the go with our convenient and free mobile app</li>
                                    <li>Fast response times from verified and top rated local suppliers.</li>
                                    </ul>
                                </Tooltip>
                            }>
                                <Icon icon={faInfoCircle}/>
                            </OverlayTrigger>

                            Why use Easy Weddings to message suppliers?
                        </div>
                    </Show>
                    <Button type="submit" stretch disabled={(isSubmitting)} data-testid="enquiry-form-submit">
                        {isSubmitting ? "Sending" : "Request Quote"}
                    </Button>
                    <div className="small submit-privacy lh-base mt-2">
                        By submitting this form you are registering and agreeing to
                        the{" "}
                        <a href="/privacy/" rel="nofollow">
                            Terms and Conditions.
                        </a>{" "}
                        and{" "}
                        <a href="/privacy/" rel="nofollow">
                            Privacy Policy.
                        </a>
                    </div>
                </div>
            </div>
        </form>
    );
}

export default EnquiryForm;
